import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, Skeleton } from "@repo/ui";
import { LogOut } from "lucide-react";
import { SubscriptionPlans } from "@/components/subscribe/subscription-plans";
import { SubscribeExtras } from "@/components/subscribe/subscribe-extras";
import {
  createCheckoutSession,
  getPlanPrices,
  type PlanPrices,
} from "@/lib/api/stripe";
import { logout } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/auth-provider";

const FALLBACK_PRICES: PlanPrices = {
  monthly: { amount: 15000, currency: "usd", interval: "month" },
  yearly: { amount: 150000, currency: "usd", interval: "year" },
};

export const Route = createFileRoute("/_auth/subscribe")({
  component: SubscribePage,
});

function SubscribePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [error, setError] = useState("");
  const [prices, setPrices] = useState<PlanPrices | null>(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);

  // Pull the pharmacy name from the auth context rather than re-fetching it
  // from /pharmacy/settings. The settings endpoint requires an active staff
  // user, and this page is reachable before one is selected — hitting it
  // triggers a 401 → /select-user → /dashboard → /subscribe loop for any
  // pharmacy with needsFirstUser=true and an unpaid subscription status.
  const pharmacyName = auth.pharmacy?.name ?? "Your Pharmacy";

  useEffect(() => {
    let cancelled = false;

    const fetchPrices = async () => {
      setIsLoadingPrices(true);
      try {
        const result = await getPlanPrices();
        if (!cancelled) setPrices(result);
      } catch {
        if (!cancelled) setPrices(FALLBACK_PRICES);
      } finally {
        if (!cancelled) setIsLoadingPrices(false);
      }
    };

    fetchPrices();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelectPlan = async (plan: "monthly" | "yearly") => {
    setError("");
    try {
      const data = (await createCheckoutSession({
        plan,
      })) as { url?: string };

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const apiError = err as {
        statusCode?: number;
        error?: string;
        message?: string;
      };
      setError(
        apiError.error ||
          apiError.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      sessionStorage.clear();
      navigate({ to: "/login" });
    } catch {
      navigate({ to: "/login" });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background h-screen overflow-y-auto">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 flex h-16 items-center justify-between bg-surface px-4 md:px-6 mx-2 mt-2 rounded-xl shadow-sm">
        {/* Left: Logo */}
        <div className="flex items-center">
          <img
            src="/logo-with-text-blue.png"
            alt="C2 Vault"
            className="h-7 w-auto"
          />
        </div>

        {/* Center / Right: Pharmacy name + Logout */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 rounded-lg bg-primary/5 px-3 py-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold">
              {pharmacyName.substring(0, 1).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-foreground">
              {pharmacyName}
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline ml-1.5">Log out</span>
          </Button>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1">
        <div className="relative px-4 py-12 sm:py-16 md:py-20">
          {/* Error toast */}
          {error && (
            <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl border border-destructive/50 bg-destructive/10 text-destructive text-sm shadow-lg backdrop-blur-sm">
              {error}
            </div>
          )}

          {isLoadingPrices ? (
            <div className="w-full max-w-2xl mx-auto space-y-6">
              <div className="text-center space-y-3">
                <Skeleton className="h-8 w-48 mx-auto rounded-full" />
                <Skeleton className="h-10 w-72 mx-auto" />
                <Skeleton className="h-5 w-64 mx-auto" />
              </div>
              <Skeleton className="h-6 w-40 mx-auto rounded-full" />
              <Skeleton className="h-[420px] w-full rounded-2xl" />
            </div>
          ) : prices ? (
            <>
              <SubscriptionPlans
                pharmacyName={pharmacyName}
                prices={prices}
                onSelectPlan={handleSelectPlan}
              />
              <SubscribeExtras />
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
